import React, { useEffect, useRef, useState } from 'react';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { employeeService, getCandidateSubmission, updateCandidateSubmission } from '../services/employeeService';
import { getPresignedUrl, uploadFile, getViewPresignedUrl } from '../services/fileService';
import { getPositions } from '../services/positionsService';
import { sendDraftLinkEmail } from '../services/notificationService';
import { EmployeeRecord, FieldFlag, DocumentType, DocumentAttachment, Position, POSITION_OPTIONS, QUALIFICATION_OPTIONS, LOCATIONS_OPTIONS, RELATION_OPTIONS } from '../types';
import { FiPlus, FiTrash2, FiCheck, FiCopy, FiAlertCircle, FiX, FiUpload, FiUser, FiChevronDown, FiRotateCcw, FiSend } from 'react-icons/fi';
import { Listbox, Transition } from '@headlessui/react';
import { Fragment } from 'react';
import { extractErrorMessage, reportFormError } from '../utils/errorHandling';

type FormValues = Omit<EmployeeRecord, 'id' | 'createdAt' | 'updatedAt' | 'createdBy' | 'updatedBy' | 'createdByName' | 'updatedByName'>;

interface EmployeeFormPageProps {
  isResubmission?: boolean;
}

const EmployeeFormPage: React.FC<EmployeeFormPageProps> = ({ isResubmission = false }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isUploading, setIsUploading] = useState<Record<string, boolean>>({});
  const [employeeId, setEmployeeId] = useState<string | null>(null);
  const [submissionConfirmed, setSubmissionConfirmed] = useState(false);
  const [referenceNumber, setReferenceNumber] = useState<string>('');
  const [fieldFlags, setFieldFlags] = useState<FieldFlag[]>([]);
  const [pendingFile, setPendingFile] = useState<File | null>(null);
  const [selectedDocumentType, setSelectedDocumentType] = useState<DocumentType>('ID');
  const [profileImageUrl, setProfileImageUrl] = useState<string>('');
  const [profileImageFile, setProfileImageFile] = useState<File | null>(null);
  const [availablePositions, setAvailablePositions] = useState<Position[]>([]);
  const [showDraftLinkModal, setShowDraftLinkModal] = useState(false);
  const [draftEmail, setDraftEmail] = useState('');
  const [draftLinkSending, setDraftLinkSending] = useState(false);
  const [latestDraftId, setLatestDraftId] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    control,
    reset,
    setValue,
    getValues,
    watch,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      dependents: [],
      attachments: [],
      maritalStatus: 'Single',
      employmentStatus: 'Unemployed',
      previouslyEmployedByTrigon: 'No',
      gender: 'Male',
      nationality: 'Namibian',
      criminalRecords: 'No',
      preExistingDisability: 'No',
      qualification: 'None',
      positionAppliedFor: [],
      residentialAddress: {
        streetAddress: '',
        townCity: '',
        customTownCity: '',
      },
    },
  });

  const { fields: dependentFields, append: appendDependent, remove: removeDependent } = useFieldArray({
    control,
    name: 'dependents',
  });



  const { fields: attachmentFields, append: appendAttachment, remove: removeAttachment } = useFieldArray({
    control,
    name: 'attachments',
  });

  // Validation patterns
  const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phonePattern = /^[\+]?[0-9\s\-\(\)]{10,15}$/;
  const numericPattern = /^[0-9]+$/;

  // Required documents
  const requiredDocumentTypes: DocumentType[] = [
    'ID',
    'Birth Certificate',
    'Police Clearance Certificate',
    'Proof of Address',
    'CV'
  ];

  // Optional documents
  const optionalDocumentTypes: DocumentType[] = [
    'Driver\'s License',
    'Bank Confirmation Letter',
    'ITAS Registration Certificate',
    'Social Security Card',
    'Qualifications',
    'Other Certifications'
  ];

  // Load available positions
  useEffect(() => {
    const loadPositions = async () => {
      try {
        const positions = await getPositions({ activeOnly: true });
        setAvailablePositions(positions);
      } catch (error) {
        console.error('Failed to load positions:', error);
        // Fallback to static options if API fails
        setAvailablePositions(POSITION_OPTIONS.map((name, index) => ({
          id: `legacy-${index}`,
          name,
          description: '',
          isActive: true,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          createdBy: 'system',
          updatedBy: 'system'
        })));
      }
    };

    loadPositions();
  }, []);

  useEffect(() => {
    // Handle resubmission flow
    const mode = searchParams.get('mode');
    const isUpdateMode = mode === 'update';
    
    if (isResubmission || isUpdateMode) {
      const token = searchParams.get('token');
      const employeeId = searchParams.get('id');
      
      if (isResubmission && employeeId) {
        // Handle resubmission flow with token (legacy)
        setEmployeeId(employeeId);
        setIsLoading(true);
        
        // Load both employee data and field flags from database
        Promise.all([
          employeeService.getEmployee(employeeId),
          employeeService.getFieldFlags(employeeId).catch((error) => {
            // If 403, user doesn't have permission (likely a candidate) - return empty array
            if (error.response?.status === 403) {
              console.log('User does not have permission to view field flags');
              return [];
            }
            throw error;
          })
        ])
          .then(([employeeData, flagsData]) => {
            const mappedData = {
              ...employeeData,
              attachments: employeeData.attachments || [],
              dependents: employeeData.dependents || [],
              positionAppliedFor: Array.isArray(employeeData.positionAppliedFor) ? employeeData.positionAppliedFor : (employeeData.positionAppliedFor ? [employeeData.positionAppliedFor] : []),
              residentialAddress: employeeData.residentialAddress || {
                streetAddress: '',
                townCity: '',
                customTownCity: '',
                      },
            };
            reset(mappedData as FormValues);
            setFieldFlags(flagsData);
            console.log('Loaded field flags from database:', flagsData);

            // Generate presigned URL for profile image if it exists
            if (employeeData.profilePhoto && employeeData.profilePhoto.fileKey) {
              return getViewPresignedUrl(employeeData.profilePhoto.fileKey);
            }
          })
          .then((presignedUrl) => {
            if (presignedUrl) {
              setProfileImageUrl(presignedUrl);
              console.log('Generated presigned URL for profile image:', presignedUrl);
            }
          })
          .catch((error) => {
            // For resubmission flow, always show error - don't clear session
            toast.error('Failed to load employee data and field flags.');
          })
          .finally(() => setIsLoading(false));
      } else if (isUpdateMode) {
        // For update mode, get the candidate's own submission
        setIsLoading(true);
        
        getCandidateSubmission()
          .then((response) => {
            const employeeData = response.data;
            setEmployeeId(employeeData.id);
            const mappedData = {
              ...employeeData,
              attachments: employeeData.attachments || [],
              dependents: employeeData.dependents || [],
              positionAppliedFor: Array.isArray(employeeData.positionAppliedFor) ? employeeData.positionAppliedFor : (employeeData.positionAppliedFor ? [employeeData.positionAppliedFor] : []),
              residentialAddress: employeeData.residentialAddress || {
                streetAddress: '',
                townCity: '',
                customTownCity: '',
                      },
            };
            reset(mappedData as FormValues);
            
            // Now load field flags with the correct employee ID
            return employeeService.getFieldFlags(employeeData.id).catch((error) => {
              // If 403, user doesn't have permission (likely a candidate) - return empty array
              if (error.response?.status === 403) {
                console.log('User does not have permission to view field flags');
                return [];
              }
              throw error;
            });
          })
          .then((flagsData) => {
            setFieldFlags(flagsData);
            console.log('Loaded field flags from database:', flagsData);
            
            // Generate presigned URL for profile image if it exists
            const profilePhoto = watch('profilePhoto');
            if (profilePhoto && profilePhoto.fileKey) {
              return getViewPresignedUrl(profilePhoto.fileKey);
            }
          })
          .then((presignedUrl) => {
            if (presignedUrl) {
              setProfileImageUrl(presignedUrl);
              console.log('Generated presigned URL for profile image:', presignedUrl);
            }
          })
          .catch((error) => {
            // For update mode, always show error - don't clear session
            toast.error('Failed to load employee data and field flags.');
          })
          .finally(() => setIsLoading(false));
      }
    } else {
      // Regular flow
      const draftIdParam = searchParams.get('draftId');
      const storedId = draftIdParam || localStorage.getItem('draftEmployeeId');

      if (draftIdParam) {
        localStorage.setItem('draftEmployeeId', draftIdParam);
      }

      if (storedId) {
        setEmployeeId(storedId);
        setLatestDraftId(storedId);
        setIsLoading(true);
        employeeService.getEmployeeDraftOnly(storedId)
          .then(data => {
            const mappedData = {
              ...data,
              attachments: data.attachments || [],
              dependents: data.dependents || [],
              positionAppliedFor: Array.isArray(data.positionAppliedFor) ? data.positionAppliedFor : (data.positionAppliedFor ? [data.positionAppliedFor] : []),
              residentialAddress: data.residentialAddress || {
                streetAddress: '',
                townCity: '',
                customTownCity: '',
                      },
            };
            reset(mappedData as FormValues);
            if (data.email) {
              setDraftEmail(data.email);
            }
          })
          .catch((error) => {
            // If employee not found, clear the session and start fresh (clean apply)
            if (error.response?.data?.error === 'Employee not found') {
              localStorage.removeItem('draftEmployeeId');
              setEmployeeId(null);
              // Don't show error, just start with a clean form
            } else {
              toast.error('Failed to load employee data.');
            }
          })
          .finally(() => setIsLoading(false));
      }
    }
  }, [reset, isResubmission, searchParams]);

  // Handle pre-filled email from URL (for orphaned users)
  useEffect(() => {
    const emailParam = searchParams.get('email');
    if (emailParam && !isResubmission) {
      // Pre-fill the email field for orphaned users being redirected to registration
      setValue('email', emailParam);
      toast.success('Please complete your application with the pre-filled email address.');
    }
  }, [searchParams, setValue, isResubmission]);

  // Handle direct access to /resubmit route (for logged-in candidates)
  useEffect(() => {
    // If this is the resubmit page and we're not in resubmission mode with URL params,
    // load the candidate's own submission
    if (isResubmission && !searchParams.get('id') && !searchParams.get('token')) {
      setIsLoading(true);
      
      getCandidateSubmission()
        .then((response) => {
          const employeeData = response.data;
          setEmployeeId(employeeData.id);
          const mappedData = {
            ...employeeData,
            attachments: employeeData.attachments || [],
            dependents: employeeData.dependents || [],
          };
          reset(mappedData as FormValues);
          
          // Now load field flags with the correct employee ID
          return employeeService.getFieldFlags(employeeData.id).catch((error) => {
            // If 403, user doesn't have permission (likely a candidate) - return empty array
            if (error.response?.status === 403) {
              console.log('User does not have permission to view field flags');
              return [];
            }
            throw error;
          });
        })
        .then((flagsData) => {
          setFieldFlags(flagsData);
          console.log('Loaded field flags from database:', flagsData);
          
          // Generate presigned URL for profile image if it exists
          const profilePhoto = watch('profilePhoto');
          if (profilePhoto && profilePhoto.fileKey) {
            return getViewPresignedUrl(profilePhoto.fileKey);
          }
        })
        .then((presignedUrl) => {
          if (presignedUrl) {
            setProfileImageUrl(presignedUrl);
            console.log('Generated presigned URL for profile image:', presignedUrl);
          }
        })
        .catch((error) => {
          console.error('Failed to load candidate submission:', error);
          toast.error('Failed to load your application. Please try again.');
        })
        .finally(() => setIsLoading(false));
    }
  }, [isResubmission, reset]);

  // Monitor profile photo changes and cleanup local URLs
  useEffect(() => {
    const profilePhoto = watch('profilePhoto');
    console.log('👀 Profile photo changed in form:', profilePhoto);
    if (profilePhoto) {
      console.log('📋 Profile photo details:', {
        id: profilePhoto.id,
        fileName: profilePhoto.fileName,
        fileKey: profilePhoto.fileKey,
        downloadUrl: profilePhoto.downloadUrl,
        hasDownloadUrl: !!profilePhoto.downloadUrl
      });
    } else {
      // Clean up local URL when profile photo is removed
      if (profileImageUrl && profileImageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(profileImageUrl);
        setProfileImageUrl('');
        setProfileImageFile(null);
        console.log('🧹 Cleaned up local profile image URL');
      }
    }
  }, [watch('profilePhoto'), profileImageUrl]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (profileImageUrl && profileImageUrl.startsWith('blob:')) {
        URL.revokeObjectURL(profileImageUrl);
        console.log('🧹 Cleaned up local profile image URL on unmount');
      }
    };
  }, [profileImageUrl]);

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setPendingFile(file);
      // Auto-upload the file
      await handleFileUpload(file);
    }
  };

  const handleProfilePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type for profile photos
      const allowedImageTypes = ['image/jpeg', 'image/jpg', 'image/png'];
      if (!allowedImageTypes.includes(file.type)) {
        toast.error('Profile photo must be a JPG or PNG image file.');
        return;
      }
      // Check file size (max 5MB for profile photos)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('Profile photo must be less than 5MB.');
        return;
      }
      setProfileImageFile(file);
      
      // Auto-upload the file
      await handleProfilePhotoUpload(file);
    }
  };

  // A file can't be uploaded until a record exists to attach it to: the
  // backend's anonymous-upload check rejects uploads to a non-existent
  // employeeId (previously we sent the placeholder 'new', which 403'd as
  // "Invalid employee record"). So on the first upload of a fresh
  // registration, transparently create a draft record and use its real id.
  //
  // The ref dedupes concurrent calls — selecting several files at once must
  // not create several drafts, since `employeeId` state hasn't committed yet
  // when the next handler runs.
  const draftCreationRef = useRef<Promise<string> | null>(null);

  const ensureDraftId = async (): Promise<string> => {
    if (employeeId) return employeeId;
    if (draftCreationRef.current) return draftCreationRef.current;

    const creation = (async () => {
      const data = getValues();
      const created = await employeeService.createEmployeeDraft({ ...data, status: 'draft' });
      setEmployeeId(created.id);
      setLatestDraftId(created.id);
      localStorage.setItem('draftEmployeeId', created.id);
      return created.id;
    })();

    draftCreationRef.current = creation;
    try {
      return await creation;
    } catch (error) {
      // Allow a later upload to retry draft creation.
      draftCreationRef.current = null;
      throw error;
    }
  };

  const handleFileUpload = async (fileToUpload?: File) => {
    const file = fileToUpload || pendingFile;
    if (!file) return;

    const uploadId = Date.now().toString();
    setIsUploading(prev => ({ ...prev, [uploadId]: true }));
    
    try {
      const tempId = await ensureDraftId();
      console.log('🚀 Starting file upload for:', selectedDocumentType);
      console.log('📁 File details:', { name: file.name, type: file.type, size: file.size });
      console.log('🆔 Employee ID:', tempId);
      
      const response = await getPresignedUrl(file.name, file.type, tempId, selectedDocumentType);
      console.log('📤 Presigned URL response:', response);
      
      await uploadFile(response.uploadUrl, file);
      console.log('✅ File uploaded to S3 successfully');
      
      const newAttachment: DocumentAttachment = {
        id: uploadId,
        fileName: file.name,
        fileType: file.type,
        documentType: selectedDocumentType,
        fileKey: response.fileKey,
        downloadUrl: response.downloadUrl,
        uploadedAt: new Date().toISOString(),
      };
      
      console.log('📎 Created attachment object:', newAttachment);
      
      appendAttachment(newAttachment);
      toast.success(`${selectedDocumentType} uploaded successfully.`);
      setPendingFile(null);
      
      // Reset file input
      const fileInput = document.getElementById('file-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      
    } catch (error) {
      console.error('❌ Upload error:', error);
      toast.error(`Failed to upload ${selectedDocumentType}.`);
    } finally {
      setIsUploading(prev => ({ ...prev, [uploadId]: false }));
    }
  };

  const handleProfilePhotoUpload = async (fileToUpload?: File) => {
    const file = fileToUpload || profileImageFile;
    if (!file) return;

    const uploadId = Date.now().toString();
    setIsUploading(prev => ({ ...prev, 'profile-photo': true }));
    
    try {
      const tempId = await ensureDraftId();
      console.log('🚀 Starting profile photo upload');
      console.log('📁 File details:', { name: file.name, type: file.type, size: file.size });
      console.log('🆔 Employee ID:', tempId);
      
      const response = await getPresignedUrl(file.name, file.type, tempId, 'Profile Photo');
      console.log('📤 Presigned URL response:', response);
      
      await uploadFile(response.uploadUrl, file);
      console.log('✅ Profile photo uploaded to S3 successfully');
      
      const newProfilePhoto: DocumentAttachment = {
        id: uploadId,
        fileName: file.name,
        fileType: file.type,
        documentType: 'Profile Photo' as any, // Type assertion since we removed it from DocumentType
        fileKey: response.fileKey,
        downloadUrl: response.downloadUrl,
        uploadedAt: new Date().toISOString(),
      };
      
      console.log('🖼️ Setting profile photo in form...');
      setValue('profilePhoto', newProfilePhoto);
      console.log('✅ Profile photo set in form');
      
      // Create local URL for immediate display
      const localUrl = URL.createObjectURL(file);
      setProfileImageUrl(localUrl);
      console.log('🖼️ Set local profile image URL:', localUrl);
      
      toast.success('Profile photo uploaded successfully.');
      setProfileImageFile(null);
      
      // Reset file input
      const fileInput = document.getElementById('profile-photo-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
      
    } catch (error) {
      console.error('❌ Profile photo upload error:', error);
      toast.error('Failed to upload profile photo.');
    } finally {
      setIsUploading(prev => ({ ...prev, 'profile-photo': false }));
    }
  };

  const saveDraft = async () => {
    const data = getValues();
    setIsLoading(true);
    try {
      let currentDraftId = employeeId;
      if (employeeId) {
        // For existing records, use smart update method that determines draft vs authenticated
        console.log('Updating existing draft with ID:', employeeId);
        await employeeService.updateEmployeeSmartly(employeeId, data);
      } else {
        // For new records, create with draft status using anonymous API
        console.log('Creating new draft');
        const created = await employeeService.createEmployeeDraft({ ...data, status: 'draft' });
        currentDraftId = created.id;
        setEmployeeId(created.id);
        localStorage.setItem('draftEmployeeId', created.id);
      }
      
      if (currentDraftId) {
        setLatestDraftId(currentDraftId);
      }
      setDraftEmail(data.email || draftEmail || '');
      setShowDraftLinkModal(true);
      toast.success('Draft saved successfully.');
    } catch (error: any) {
      console.error('Save draft error:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      toast.error(`Failed to save draft: ${error.response?.data?.error || error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  const clearForm = () => {
    const confirmed = window.confirm(
      'Are you sure you want to clear the entire form? This will remove all entered data and cannot be undone.'
    );
    
    if (!confirmed) {
      return;
    }
    
    // Clear localStorage
    localStorage.removeItem('draftEmployeeId');

    // Reset form to default values
    reset({
      dependents: [],
      attachments: [],
      maritalStatus: 'Single',
      employmentStatus: 'Unemployed',
      previouslyEmployedByTrigon: 'No',
      gender: 'Male',
      nationality: 'Namibian',
      criminalRecords: 'No',
      preExistingDisability: 'No',
      qualification: 'None',
      positionAppliedFor: [],
      residentialAddress: {
        streetAddress: '',
        townCity: '',
        customTownCity: '',
      },
    });
    
    // Clear additional state
    setEmployeeId(null);
    draftCreationRef.current = null;
    setFieldFlags([]);
    setProfileImageUrl('');
    setProfileImageFile(null);
    setPendingFile(null);
    setSelectedDocumentType('ID');
    
    toast.success('Form cleared successfully.');
  };

  const onSubmit = async (data: FormValues) => {
    // Clean phone number (remove spaces, dashes, parentheses)
    if (data.contactNumber) {
      data.contactNumber = data.contactNumber.replace(/[^\d+]/g, '');
    }

    // Validate required attachments
    const uploadedDocumentTypes = data.attachments.map(att => att.documentType);
    const missingDocuments = requiredDocumentTypes.filter(type => !uploadedDocumentTypes.includes(type as DocumentType));
    
    if (missingDocuments.length > 0) {
      const message = `Please upload the following required documents: ${missingDocuments.join(', ')}`;
      toast.error(message);
      reportFormError({
        formName: 'Employee Application',
        message,
        category: 'validation',
        validationErrors: missingDocuments.map(doc => ({
          field: 'attachments',
          message: `${doc} document is required`,
        })),
        tags: ['employee-form', 'documents'],
        userEmail: data.email,
      });
      return;
    }

    setIsLoading(true);
    const mode = searchParams.get('mode');
    const isUpdateMode = mode === 'update';

    try {
      let result;
      
      if (employeeId) {
        if (isUpdateMode || isResubmission) {
          // For update mode or resubmit, use the candidate submission endpoint
          result = await updateCandidateSubmission({ ...data, status: 'under_review' });
          setReferenceNumber(employeeId);
        } else {
          result = await employeeService.updateEmployeeSmartly(employeeId, { ...data, status: 'new_submission' });
          setReferenceNumber(employeeId);
        }
      } else {
        result = await employeeService.createEmployee({ ...data, status: 'new_submission' });
        setReferenceNumber(result.id);
      }
      
      localStorage.removeItem('draftEmployeeId');
      setSubmissionConfirmed(true);
      
      if (isResubmission || isUpdateMode) {
        toast.success('Application updated successfully!');
      } else {
        toast.success('Application submitted successfully!');
      }
    } catch (error) {
      const failureMessage = extractErrorMessage(
        error,
        'Failed to submit application. Please try again.',
      );
      toast.error(failureMessage);
      reportFormError({
        formName: 'Employee Application',
        error,
        message: failureMessage,
        userEmail: data.email,
        tags: ['employee-form'],
        details: JSON.stringify({
          employeeId,
          submissionMode: isResubmission ? 'resubmission' : mode || 'new',
        }),
      });
    } finally {
      setIsLoading(false);
    }
  };

  const copyReferenceNumber = () => {
    navigator.clipboard.writeText(referenceNumber);
    toast.success('Reference number copied to clipboard!');
  };

  const buildDraftLinkUrl = () => {
    const idToUse = latestDraftId || employeeId;
    if (!idToUse) return '';
    return `${window.location.origin}/register?draftId=${idToUse}`;
  };

  const handleSendDraftLink = async () => {
    if (!draftEmail || !emailPattern.test(draftEmail)) {
      toast.error('Please enter a valid email address to send the draft link.');
      return;
    }

    const idToUse = latestDraftId || employeeId;
    if (!idToUse) {
      toast.error('Draft ID unavailable. Please save the draft again.');
      return;
    }

    const candidateName = `${getValues('fullNames') || ''} ${getValues('surname') || ''}`.trim();
    const draftLink = buildDraftLinkUrl();

    setDraftLinkSending(true);
    try {
      await sendDraftLinkEmail(draftEmail, draftLink, idToUse, candidateName || undefined);
      toast.success('Draft link sent to your email.');
      setShowDraftLinkModal(false);
    } catch (error) {
      console.error('Failed to send draft link email:', error);
      toast.error('Could not send the draft link. Please try again.');
    } finally {
      setDraftLinkSending(false);
    }
  };

  if (isLoading && employeeId) {
    return <div>Loading...</div>;
  }

  // Show confirmation screen after submission
  if (submissionConfirmed) {
    return (
      <div className="max-w-2xl mx-auto">
        <div className="bg-white shadow rounded-lg p-8 text-center">
          <div className="mb-6">
            <FiCheck className="mx-auto h-16 w-16 text-green-500" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            {isResubmission ? 'Application Updated!' : 'Application Submitted!'}
          </h1>
          <p className="text-gray-600 mb-6">
            {isResubmission 
              ? 'Your application updates have been received and will be reviewed by our HR team.'
              : 'Thank you for submitting your registration. We have received your submission and will review it shortly.'
            }
          </p>
          <div className="bg-gray-50 border rounded-lg p-4 mb-6">
            <p className="text-sm text-gray-600 mb-2">Your Reference Number:</p>
            <div className="flex items-center justify-center gap-2">
              <span className="text-2xl font-mono font-bold text-blue-600">{referenceNumber}</span>
              <button
                onClick={copyReferenceNumber}
                className="p-2 text-gray-500 hover:text-blue-600 transition-colors"
                title="Copy reference number"
              >
                <FiCopy />
              </button>
            </div>
          </div>
          <p className="text-sm text-gray-500 mb-6">
            Please save this reference number for your records. You'll need it to check your application status.
          </p>
          <div className="space-y-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="w-full px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              Go to Dashboard
            </button>
            <button
              onClick={() => {
                setSubmissionConfirmed(false);
                setReferenceNumber('');
                reset({
                  dependents: [],
                  attachments: [],
                  maritalStatus: 'Single',
                  employmentStatus: 'Unemployed',
                  previouslyEmployedByTrigon: 'No',
                  gender: 'Male',
                  nationality: 'Namibian',
                  criminalRecords: 'No',
                  preExistingDisability: 'No',
                  qualification: 'None',
                  positionAppliedFor: [],
                  residentialAddress: {
                    streetAddress: '',
                    townCity: '',
                    customTownCity: '',
                              },
                });
              }}
              className="w-full px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
            >
              Submit Another Application
            </button>
          </div>
        </div>
      </div>
    );
  }

  const fieldToSectionMap: Record<string, string> = {
    // Personal Details section
    'fullNames': 'personalDetails',
    'alsoKnownAs': 'personalDetails',
    'surname': 'personalDetails',
    'maidenName': 'personalDetails',
    'email': 'personalDetails',
    'contactNumber': 'personalDetails',
    'dateOfBirth': 'personalDetails',
    'gender': 'personalDetails',
    'nationality': 'personalDetails',
    'idNumber': 'personalDetails',
    'maritalStatus': 'personalDetails',
    'positionAppliedFor': 'personalDetails',
    'employmentStatus': 'employmentStatus',
    'currentEmployer': 'employmentStatus',
    'currentPosition': 'employmentStatus',
    'previouslyEmployedByTrigon': 'employmentStatus',
    'previousTrigonPosition': 'employmentStatus',
    'previousTrigonEmployeeNumber': 'employmentStatus',
    'criminalRecords': 'personalDetails',
    'preExistingDisability': 'personalDetails',
    'disabilityDescription': 'personalDetails',
    'qualification': 'personalDetails',
    
    // Address section
    'residentialAddress': 'address',
    'residentialAddress.streetAddress': 'address',
    'residentialAddress.townCity': 'address',
    'residentialAddress.customTownCity': 'address',
    
    // Banking section
    'bankingDetails.bank': 'banking',
    'bankingDetails.branch': 'banking',
    'bankingDetails.accountNumber': 'banking',
    'bankingDetails.accountType': 'banking',
    'bankingDetails.accountName': 'banking',
    
    // Tax section  
    'taxNumber': 'tax',
    'socialSecurityNumber': 'tax',
    
    // Documents section
    'attachments': 'documents',
  };

  const getFieldFlag = (fieldName: string): FieldFlag | undefined => {
    // Ensure fieldFlags is an array
    if (!Array.isArray(fieldFlags)) {
      console.warn('fieldFlags is not an array:', fieldFlags);
      return undefined;
    }
    
    // Check if the specific field is flagged
    let flag = fieldFlags.find(flag => flag.fieldPath === fieldName);
    if (flag) {
      return flag;
    }
    
    // Check if the section containing this field is flagged
    const section = fieldToSectionMap[fieldName];
    return section ? fieldFlags.find(flag => flag.fieldPath === section) : undefined;
  };

  const isFieldFlagged = (fieldName: string) => {
    return !!getFieldFlag(fieldName);
  };

  const isSectionFlagged = (sectionName: string) => {
    // Ensure fieldFlags is an array
    if (!Array.isArray(fieldFlags)) {
      console.warn('fieldFlags is not an array:', fieldFlags);
      return false;
    }
    return fieldFlags.some(flag => flag.fieldPath === sectionName);
  };

  // Component for showing field flag message
  const FieldFlagIndicator: React.FC<{ fieldName: string }> = ({ fieldName }) => {
    const flag = getFieldFlag(fieldName);
    const [showMessage, setShowMessage] = useState(false);

    // Close popup when clicking outside - must be called before early return
    useEffect(() => {
      if (!showMessage || !flag) return;

      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Element;
        if (!target.closest('.flag-popup-container')) {
          setShowMessage(false);
        }
      };

      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }, [showMessage, flag]);

    if (!flag) return null;

    return (
      <div className="relative inline-block ml-2 flag-popup-container">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setShowMessage(!showMessage);
          }}
          className="text-orange-500 hover:text-orange-600 transition-colors"
          title="Click to see what needs to be fixed"
        >
          <FiAlertCircle size={16} />
        </button>
        
        {showMessage && (
          <div className="absolute left-0 top-6 z-10 w-64 p-3 bg-orange-50 border border-orange-200 rounded-lg shadow-lg">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-orange-800 mb-1">Attention Required:</p>
                <p className="text-sm text-orange-700">{flag.message}</p>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowMessage(false);
                }}
                className="text-orange-400 hover:text-orange-600 ml-2 flex-shrink-0"
              >
                <FiX size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Component for showing field flag message for attachments (handles document type matching)
  const AttachmentFieldFlagIndicator: React.FC<{ documentType: string; index: number }> = ({ documentType, index }) => {
    // Check for field flags that match this specific document
    const flag = fieldFlags.find(flag => 
      flag.fieldPath === `attachments.${index}.${documentType}` ||
      flag.fieldPath === `attachments.${index}` ||
      (flag.fieldPath.includes('attachments') && flag.fieldPath.includes(documentType))
    );
    
    const [showMessage, setShowMessage] = useState(false);

    // Close popup when clicking outside
    useEffect(() => {
      if (!showMessage || !flag) return;

      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Element;
        if (!target.closest('.flag-popup-container')) {
          setShowMessage(false);
        }
      };

      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }, [showMessage, flag]);

    if (!flag) return null;

    return (
      <div className="relative inline-block ml-2 flag-popup-container">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            setShowMessage(!showMessage);
          }}
          className="text-orange-500 hover:text-orange-600 transition-colors"
          title="Click to see what needs to be fixed"
        >
          <FiAlertCircle size={16} />
        </button>
        
        {showMessage && (
          <div className="absolute left-0 top-6 z-10 w-64 p-3 bg-orange-50 border border-orange-200 rounded-lg shadow-lg">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm font-medium text-orange-800 mb-1">Attention Required:</p>
                <p className="text-sm text-orange-700">{flag.message}</p>
              </div>
              <button
                type="button"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setShowMessage(false);
                }}
                className="text-orange-400 hover:text-orange-600 ml-2 flex-shrink-0"
              >
                <FiX size={14} />
              </button>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {showDraftLinkModal && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center px-4">
          <div className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6">
            <div className="flex items-start justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Send draft link to your email?</h3>
              <button
                type="button"
                onClick={() => setShowDraftLinkModal(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <FiX />
              </button>
            </div>
            <p className="text-sm text-gray-600 mb-4">
              We can email you a temporary link so you can reopen this draft later. You can also share it with the candidate if you are preparing the form on their behalf.
            </p>
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">Email address</label>
              <input
                type="email"
                value={draftEmail}
                onChange={(e) => setDraftEmail(e.target.value)}
                placeholder="you@example.com"
                className="w-full border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            {buildDraftLinkUrl() && (
              <div className="mt-3 text-xs text-gray-500">
                Draft link: <span className="font-mono break-all">{buildDraftLinkUrl()}</span>
              </div>
            )}
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={() => setShowDraftLinkModal(false)}
                className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 hover:bg-gray-50"
              >
                Not now
              </button>
              <button
                type="button"
                onClick={handleSendDraftLink}
                disabled={draftLinkSending}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60 inline-flex items-center"
              >
                <FiSend className="mr-2" />
                {draftLinkSending ? 'Sending...' : 'Send link'}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="space-y-8">
      {isResubmission && Array.isArray(fieldFlags) && fieldFlags.length > 0 && (
        <div className="bg-orange-50 border-l-4 border-orange-400 p-4">
          <div className="flex">
            <div className="ml-3">
              <p className="text-sm text-orange-700">
                <strong>Attention Required:</strong> Please review and update the highlighted sections below based on feedback from our HR team.
              </p>
              <p className="text-sm text-orange-600 mt-2">
                Flagged fields: <strong>{fieldFlags.map(flag => 
                  flag.fieldPath.charAt(0).toUpperCase() + flag.fieldPath.slice(1).replace(/([A-Z])/g, ' $1')
                ).join(', ')}</strong>
              </p>
            </div>
          </div>
        </div>
      )}
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Personal Details */}
        <div className={`bg-white shadow rounded-lg p-6 ${isSectionFlagged('personalDetails') ? 'border-l-4 border-orange-400 bg-orange-50' : ''}`}>
          <h2 className={`text-xl font-bold mb-4 ${isSectionFlagged('personalDetails') ? 'text-orange-800' : ''}`}>
            Personal Details
            {isSectionFlagged('personalDetails') && <span className="ml-2 text-sm text-orange-600">(Requires Attention)</span>}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                Full Names <span className="text-red-500 ml-1">*</span>
                <FieldFlagIndicator fieldName="fullNames" />
              </label>
              <input 
                {...register('fullNames', { required: 'Full names are required' })} 
                placeholder="Full Names" 
                className={`p-2 border rounded w-full ${isFieldFlagged('fullNames') ? 'border-orange-400 bg-orange-50' : 'border-gray-300'}`} 
              />
              {errors.fullNames && <p className="text-red-500 text-xs mt-1">{errors.fullNames.message}</p>}
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                Also Known As
                <FieldFlagIndicator fieldName="alsoKnownAs" />
              </label>
              <input 
                {...register('alsoKnownAs')} 
                placeholder="Also Known As" 
                className={`p-2 border rounded w-full ${isFieldFlagged('alsoKnownAs') ? 'border-orange-400 bg-orange-50' : 'border-gray-300'}`} 
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                Surname <span className="text-red-500 ml-1">*</span>
                <FieldFlagIndicator fieldName="surname" />
              </label>
              <input 
                {...register('surname', { required: 'Surname is required' })} 
                placeholder="Surname" 
                className={`p-2 border rounded w-full ${isFieldFlagged('surname') ? 'border-orange-400 bg-orange-50' : 'border-gray-300'}`} 
              />
              {errors.surname && <p className="text-red-500 text-xs mt-1">{errors.surname.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                Maiden Name
                <FieldFlagIndicator fieldName="maidenName" />
              </label>
              <input 
                {...register('maidenName')} 
                placeholder="Maiden Name" 
                className={`p-2 border rounded w-full ${isFieldFlagged('maidenName') ? 'border-orange-400 bg-orange-50' : 'border-gray-300'}`} 
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth <span className="text-red-500 ml-1">*</span></label>
              <input 
                {...register('dateOfBirth', { required: 'Date of birth is required' })} 
                type="date" 
                className={`p-2 border rounded w-full ${isFieldFlagged('dateOfBirth') ? 'border-orange-400 bg-orange-50' : 'border-gray-300'}`} 
              />
              {errors.dateOfBirth && <p className="text-red-500 text-xs mt-1">{errors.dateOfBirth.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Gender <span className="text-red-500 ml-1">*</span></label>
              <select 
                {...register('gender', { required: 'Gender is required' })} 
                className={`p-2 border rounded w-full ${isFieldFlagged('gender') ? 'border-orange-400 bg-orange-50' : 'border-gray-300'}`}
              >
                <option value="Male">Male</option>
                <option value="Female">Female</option>
              </select>
              {errors.gender && <p className="text-red-500 text-xs mt-1">{errors.gender.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                Nationality <span className="text-red-500 ml-1">*</span>
                <FieldFlagIndicator fieldName="nationality" />
              </label>
              <select 
                {...register('nationality', { required: 'Nationality is required' })} 
                className={`p-2 border rounded w-full ${isFieldFlagged('nationality') ? 'border-orange-400 bg-orange-50' : 'border-gray-300'}`}
              >
                <option value="">Select Nationality</option>
                <option value="Namibian">Namibian</option>
                <option value="South African">South African</option>
                <option value="Angolan">Angolan</option>
                <option value="Botswanan">Botswanan</option>
                <option value="Zambian">Zambian</option>
                <option value="Zimbabwean">Zimbabwean</option>
                <option value="Other">Other</option>
              </select>
              {errors.nationality && <p className="text-red-500 text-xs mt-1">{errors.nationality.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ID Number <span className="text-red-500 ml-1">*</span></label>
              <input 
                {...register('idNumber', { 
                  required: 'ID Number is required',
                  pattern: {
                    value: numericPattern,
                    message: 'ID Number must be numeric'
                  }
                })} 
                placeholder="ID Number" 
                className={`p-2 border rounded w-full ${isFieldFlagged('idNumber') ? 'border-orange-400 bg-orange-50' : 'border-gray-300'}`} 
              />
              {errors.idNumber && <p className="text-red-500 text-xs mt-1">{errors.idNumber.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                Contact Number
                <FieldFlagIndicator fieldName="contactNumber" />
              </label>
              <input
                {...register('contactNumber', {
                  pattern: {
                    value: phonePattern,
                    message: 'Please enter a valid phone number'
                  },
                  validate: (value) => {
                    const email = watch('email');
                    if (!value && !email) {
                      return 'Either phone number or email address is required';
                    }
                    return true;
                  }
                })}
                placeholder="Contact Number (optional if email provided)"
                className={`p-2 border rounded w-full ${isFieldFlagged('contactNumber') ? 'border-orange-400 bg-orange-50' : 'border-gray-300'}`}
              />
              {errors.contactNumber && <p className="text-red-500 text-xs mt-1">{errors.contactNumber.message}</p>}
              <p className="text-xs text-gray-500 mt-1">
                At least one contact method (email or phone) is required
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                Email Address
                <FieldFlagIndicator fieldName="email" />
              </label>
              <input
                {...register('email', {
                  pattern: {
                    value: emailPattern,
                    message: 'Please enter a valid email address'
                  },
                  validate: (value) => {
                    const phone = watch('contactNumber');
                    if (!value && !phone) {
                      return 'Either email address or phone number is required';
                    }
                    return true;
                  }
                })}
                placeholder="Email Address (optional if phone number provided)"
                type="email"
                className={`p-2 border rounded w-full ${isFieldFlagged('email') ? 'border-orange-400 bg-orange-50' : 'border-gray-300'}`}
              />
              {errors.email && <p className="text-red-500 text-xs mt-1">{errors.email.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                Tax Number
                <FieldFlagIndicator fieldName="taxNumber" />
              </label>
              <input
                {...register('taxNumber', {
                  pattern: {
                    value: numericPattern,
                    message: 'Tax number must be numeric'
                  }
                })}
                placeholder="Tax Number"
                type="number"
                className={`p-2 border rounded w-full ${isFieldFlagged('taxNumber') ? 'border-orange-400 bg-orange-50' : 'border-gray-300'}`}
              />
              {errors.taxNumber && <p className="text-red-500 text-xs mt-1">{errors.taxNumber.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                Social Security Number
                <FieldFlagIndicator fieldName="socialSecurityNumber" />
              </label>
              <input
                {...register('socialSecurityNumber')}
                placeholder="Social Security Number"
                className={`p-2 border rounded w-full ${isFieldFlagged('socialSecurityNumber') ? 'border-orange-400 bg-orange-50' : 'border-gray-300'}`}
              />
              {errors.socialSecurityNumber && <p className="text-red-500 text-xs mt-1">{errors.socialSecurityNumber.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Marital Status <span className="text-red-500 ml-1">*</span></label>
              <select
                {...register('maritalStatus', { required: 'Marital status is required' })}
                className={`p-2 border rounded w-full ${isFieldFlagged('maritalStatus') ? 'border-orange-400 bg-orange-50' : 'border-gray-300'}`}
              >
                <option value="Single">Single</option>
                <option value="Married">Married</option>
              </select>
              {errors.maritalStatus && <p className="text-red-500 text-xs mt-1">{errors.maritalStatus.message}</p>}
            </div>




            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                Criminal Records? <span className="text-red-500 ml-1">*</span>
                <FieldFlagIndicator fieldName="criminalRecords" />
              </label>
              <select
                {...register('criminalRecords', { required: 'This field is required' })}
                className={`p-2 border rounded w-full ${isFieldFlagged('criminalRecords') ? 'border-orange-400 bg-orange-50' : 'border-gray-300'}`}
              >
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
              {errors.criminalRecords && <p className="text-red-500 text-xs mt-1">{errors.criminalRecords.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                Pre-existing Disability? <span className="text-red-500 ml-1">*</span>
                <FieldFlagIndicator fieldName="preExistingDisability" />
              </label>
              <select
                {...register('preExistingDisability', { required: 'This field is required' })}
                className={`p-2 border rounded w-full ${isFieldFlagged('preExistingDisability') ? 'border-orange-400 bg-orange-50' : 'border-gray-300'}`}
              >
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
              {errors.preExistingDisability && <p className="text-red-500 text-xs mt-1">{errors.preExistingDisability.message}</p>}
            </div>

            {watch('preExistingDisability') === 'Yes' && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                  Disability Description <span className="text-red-500 ml-1">*</span>
                  <FieldFlagIndicator fieldName="disabilityDescription" />
                </label>
                <textarea
                  {...register('disabilityDescription', {
                    required: watch('preExistingDisability') === 'Yes' ? 'Please describe the disability' : false
                  })}
                  placeholder="Please describe the disability..."
                  rows={3}
                  className={`p-2 border rounded w-full ${isFieldFlagged('disabilityDescription') ? 'border-orange-400 bg-orange-50' : 'border-gray-300'}`}
                />
                {errors.disabilityDescription && <p className="text-red-500 text-xs mt-1">{errors.disabilityDescription.message}</p>}
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                Highest Qualification <span className="text-red-500 ml-1">*</span>
                <FieldFlagIndicator fieldName="qualification" />
              </label>
              <select
                {...register('qualification', { required: 'Highest Qualification is required' })}
                className={`p-2 border rounded w-full ${isFieldFlagged('qualification') ? 'border-orange-400 bg-orange-50' : 'border-gray-300'}`}
              >
                {QUALIFICATION_OPTIONS.map(qualification => (
                  <option key={qualification} value={qualification}>{qualification}</option>
                ))}
              </select>
              {errors.qualification && <p className="text-red-500 text-xs mt-1">{errors.qualification.message}</p>}
            </div>
          </div>
        </div>

        {/* Employment Information */}
        <div className={`bg-white shadow rounded-lg p-6 ${isSectionFlagged('employmentStatus') ? 'border-l-4 border-orange-400 bg-orange-50' : ''}`}>
          <h2 className={`text-xl font-bold mb-4 ${isSectionFlagged('employmentStatus') ? 'text-orange-800' : ''}`}>
            Employment Information
            {isSectionFlagged('employmentStatus') && <span className="ml-2 text-sm text-orange-600">(Requires Attention)</span>}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Position Applied For <span className="text-red-500 ml-1">*</span></label>
              <Controller
                name="positionAppliedFor"
                control={control}
                rules={{
                  required: 'Please select at least one position',
                  validate: (value) => (value && value.length > 0) || 'Please select at least one position'
                }}
                render={({ field: { value, onChange } }) => (
                  <Listbox value={value || []} onChange={onChange} multiple>
                    <div className="relative">
                      <Listbox.Button className={`relative w-full cursor-default rounded-lg py-2 pl-3 pr-10 text-left border ${
                        isFieldFlagged('positionAppliedFor') ? 'border-orange-400 bg-orange-50' : 'border-gray-300 bg-white'
                      } focus:outline-none focus-visible:border-indigo-500 focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-opacity-75 focus-visible:ring-offset-2 focus-visible:ring-offset-orange-300 sm:text-sm`}>
                        <span className="block truncate">
                          {!value || value.length === 0 ? (
                            <span className="text-gray-500">Select positions...</span>
                          ) : (
                            <span>{value.join(', ')}</span>
                          )}
                        </span>
                        <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                          <FiChevronDown className="h-5 w-5 text-gray-400" aria-hidden="true" />
                        </span>
                      </Listbox.Button>
                      <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                      >
                        <Listbox.Options className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm z-10">
                          {availablePositions.map((position) => (
                            <Listbox.Option
                              key={position.id}
                              className={({ active }) =>
                                `relative cursor-default select-none py-2 pl-10 pr-4 ${
                                  active ? 'bg-amber-100 text-amber-900' : 'text-gray-900'
                                }`
                              }
                              value={position.name}
                            >
                              {({ selected }) => (
                                <>
                                  <span className={`block truncate ${selected ? 'font-medium' : 'font-normal'}`}>
                                    {position.name}
                                  </span>
                                  {selected ? (
                                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-amber-600">
                                      <FiCheck className="h-5 w-5" aria-hidden="true" />
                                    </span>
                                  ) : null}
                                </>
                              )}
                            </Listbox.Option>
                          ))}
                        </Listbox.Options>
                      </Transition>
                    </div>
                  </Listbox>
                )}
              />
              <p className="text-xs text-gray-500 mt-1">Click to select multiple positions</p>
              {errors.positionAppliedFor && <p className="text-red-500 text-xs mt-1">{errors.positionAppliedFor.message}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                Employment Status <span className="text-red-500 ml-1">*</span>
                <FieldFlagIndicator fieldName="employmentStatus" />
              </label>
              <select
                {...register('employmentStatus', { required: 'Employment status is required' })}
                className={`p-2 border rounded w-full ${isFieldFlagged('employmentStatus') ? 'border-orange-400 bg-orange-50' : 'border-gray-300'}`}
              >
                <option value="Employed">Employed</option>
                <option value="Unemployed">Unemployed</option>
              </select>
              {errors.employmentStatus && <p className="text-red-500 text-xs mt-1">{errors.employmentStatus.message}</p>}
            </div>

            {watch('employmentStatus') === 'Employed' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    Current Employer
                    <FieldFlagIndicator fieldName="currentEmployer" />
                  </label>
                  <input
                    {...register('currentEmployer')}
                    placeholder="Current Employer"
                    className={`p-2 border rounded w-full ${isFieldFlagged('currentEmployer') ? 'border-orange-400 bg-orange-50' : 'border-gray-300'}`}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    Current Position
                    <FieldFlagIndicator fieldName="currentPosition" />
                  </label>
                  <input
                    {...register('currentPosition')}
                    placeholder="Current Position"
                    className={`p-2 border rounded w-full ${isFieldFlagged('currentPosition') ? 'border-orange-400 bg-orange-50' : 'border-gray-300'}`}
                  />
                </div>
              </>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                Previously Employed by Trigon Mining <span className="text-red-500 ml-1">*</span>
                <FieldFlagIndicator fieldName="previouslyEmployedByTrigon" />
              </label>
              <select
                {...register('previouslyEmployedByTrigon', { required: 'This field is required' })}
                className={`p-2 border rounded w-full ${isFieldFlagged('previouslyEmployedByTrigon') ? 'border-orange-400 bg-orange-50' : 'border-gray-300'}`}
              >
                <option value="No">No</option>
                <option value="Yes">Yes</option>
              </select>
              {errors.previouslyEmployedByTrigon && <p className="text-red-500 text-xs mt-1">{errors.previouslyEmployedByTrigon.message}</p>}
            </div>

            {watch('previouslyEmployedByTrigon') === 'Yes' && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    Previous Position at Trigon <span className="text-red-500 ml-1">*</span>
                    <FieldFlagIndicator fieldName="previousTrigonPosition" />
                  </label>
                  <input
                    {...register('previousTrigonPosition', {
                      required: watch('previouslyEmployedByTrigon') === 'Yes' ? 'Previous position is required' : false
                    })}
                    placeholder="Previous Position at Trigon"
                    className={`p-2 border rounded w-full ${isFieldFlagged('previousTrigonPosition') ? 'border-orange-400 bg-orange-50' : 'border-gray-300'}`}
                  />
                  {errors.previousTrigonPosition && <p className="text-red-500 text-xs mt-1">{errors.previousTrigonPosition.message}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                    Previous Employee Number <span className="text-red-500 ml-1">*</span>
                    <FieldFlagIndicator fieldName="previousTrigonEmployeeNumber" />
                  </label>
                  <input
                    {...register('previousTrigonEmployeeNumber', {
                      required: watch('previouslyEmployedByTrigon') === 'Yes' ? 'Previous employee number is required' : false
                    })}
                    placeholder="Previous Employee Number"
                    className={`p-2 border rounded w-full ${isFieldFlagged('previousTrigonEmployeeNumber') ? 'border-orange-400 bg-orange-50' : 'border-gray-300'}`}
                  />
                  {errors.previousTrigonEmployeeNumber && <p className="text-red-500 text-xs mt-1">{errors.previousTrigonEmployeeNumber.message}</p>}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Residential Address */}
        <div className={`bg-white shadow rounded-lg p-6 ${isSectionFlagged('address') ? 'border-l-4 border-orange-400 bg-orange-50' : ''}`}>
          <h2 className={`text-xl font-bold mb-4 ${isSectionFlagged('address') ? 'text-orange-800' : ''}`}>
            Residential Address
            {isSectionFlagged('address') && <span className="ml-2 text-sm text-orange-600">(Requires Attention)</span>}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                Street Address and Number <span className="text-red-500 ml-1">*</span>
                <FieldFlagIndicator fieldName="residentialAddress.streetAddress" />
              </label>
              <input
                {...register('residentialAddress.streetAddress', { required: 'Street address is required' })}
                placeholder="e.g., 123 Main Street, Apartment 4B"
                className={`p-2 border rounded w-full ${isFieldFlagged('residentialAddress.streetAddress') ? 'border-orange-400 bg-orange-50' : 'border-gray-300'}`}
              />
              {errors.residentialAddress?.streetAddress && <p className="text-red-500 text-xs mt-1">{errors.residentialAddress.streetAddress.message}</p>}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                Town/City <span className="text-red-500 ml-1">*</span>
                <FieldFlagIndicator fieldName="residentialAddress.townCity" />
              </label>
              <select
                {...register('residentialAddress.townCity', { required: 'Town/City is required' })}
                className={`p-2 border rounded w-full ${isFieldFlagged('residentialAddress.townCity') ? 'border-orange-400 bg-orange-50' : 'border-gray-300'}`}
              >
                <option value="">Select Town/City</option>
                {LOCATIONS_OPTIONS.map(location => (
                  <option key={location} value={location}>{location}</option>
                ))}
              </select>
              {errors.residentialAddress?.townCity && <p className="text-red-500 text-xs mt-1">{errors.residentialAddress.townCity.message}</p>}
            </div>

            {watch('residentialAddress.townCity') === 'Other' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                  Custom Town/City <span className="text-red-500 ml-1">*</span>
                  <FieldFlagIndicator fieldName="residentialAddress.customTownCity" />
                </label>
                <input
                  {...register('residentialAddress.customTownCity', {
                    required: watch('residentialAddress.townCity') === 'Other' ? 'Please specify your town/city' : false
                  })}
                  placeholder="Enter your town/city"
                  className={`p-2 border rounded w-full ${isFieldFlagged('residentialAddress.customTownCity') ? 'border-orange-400 bg-orange-50' : 'border-gray-300'}`}
                />
                {errors.residentialAddress?.customTownCity && <p className="text-red-500 text-xs mt-1">{errors.residentialAddress.customTownCity.message}</p>}
              </div>
            )}


          </div>
        </div>

        {/* Banking Details */}
        <div className={`bg-white shadow rounded-lg p-6 ${isSectionFlagged('banking') ? 'border-l-4 border-orange-400 bg-orange-50' : ''}`}>
          <h2 className={`text-xl font-bold mb-4 ${isSectionFlagged('banking') ? 'text-orange-800' : ''}`}>
            Banking Details
            {isSectionFlagged('banking') && <span className="ml-2 text-sm text-orange-600">(Requires Attention)</span>}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                Bank
                <FieldFlagIndicator fieldName="bankingDetails.bank" />
              </label>
              <input 
                {...register('bankingDetails.bank')} 
                placeholder="Bank Name" 
                className={`p-2 border rounded w-full ${isFieldFlagged('bankingDetails.bank') ? 'border-orange-400 bg-orange-50' : 'border-gray-300'}`} 
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                Branch
                <FieldFlagIndicator fieldName="bankingDetails.branch" />
              </label>
              <input 
                {...register('bankingDetails.branch')} 
                placeholder="Branch" 
                className={`p-2 border rounded w-full ${isFieldFlagged('bankingDetails.branch') ? 'border-orange-400 bg-orange-50' : 'border-gray-300'}`} 
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                Account Number
                <FieldFlagIndicator fieldName="bankingDetails.accountNumber" />
              </label>
              <input 
                {...register('bankingDetails.accountNumber')} 
                placeholder="Account Number" 
                className={`p-2 border rounded w-full ${isFieldFlagged('bankingDetails.accountNumber') ? 'border-orange-400 bg-orange-50' : 'border-gray-300'}`} 
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                Account Type
                <FieldFlagIndicator fieldName="bankingDetails.accountType" />
              </label>
              <input 
                {...register('bankingDetails.accountType')} 
                placeholder="Account Type" 
                className={`p-2 border rounded w-full ${isFieldFlagged('bankingDetails.accountType') ? 'border-orange-400 bg-orange-50' : 'border-gray-300'}`} 
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-1 flex items-center">
                Account Name
                <FieldFlagIndicator fieldName="bankingDetails.accountName" />
              </label>
              <input 
                {...register('bankingDetails.accountName')} 
                placeholder="Account Name" 
                className={`p-2 border rounded w-full ${isFieldFlagged('bankingDetails.accountName') ? 'border-orange-400 bg-orange-50' : 'border-gray-300'}`} 
              />
            </div>
          </div>
        </div>

        {/* Profile Photo */}
        <div className={`bg-white shadow rounded-lg p-6 ${isFieldFlagged('profilePhoto') ? 'border-l-4 border-orange-400 bg-orange-50' : ''}`}>
          <h2 className={`text-xl font-bold mb-4 flex items-center ${isFieldFlagged('profilePhoto') ? 'text-orange-800' : ''}`}>
            Profile Photo
            <FieldFlagIndicator fieldName="profilePhoto" />
            {isFieldFlagged('profilePhoto') && <span className="ml-2 text-sm text-orange-600">(Requires Attention)</span>}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Current Profile Photo */}
            <div>
              <h3 className="text-sm font-medium text-gray-700 mb-2">Current Photo</h3>
              {watch('profilePhoto') ? (
                <div className="relative w-48 h-48 border-2 border-gray-300 rounded-lg overflow-hidden">
                  {(() => {
                    const profilePhoto = watch('profilePhoto');
                    console.log('🖼️ Profile photo data:', profilePhoto);
                    console.log('🔗 Profile image URL:', profileImageUrl);
                    console.log('🌐 API URL:', process.env.REACT_APP_API_URL);
                    return null;
                  })()}
                  <img 
                    src={profileImageUrl || '/placeholder-avatar.png'} 
                    alt="Profile"
                    className="w-full h-full object-cover"
                    onLoad={() => {
                      console.log('✅ Image loaded successfully');
                    }}
                    onError={(e) => {
                      console.log('❌ Image failed to load');
                      console.log('🔗 Failed URL:', (e.target as HTMLImageElement).src);
                      (e.target as HTMLImageElement).src = '/placeholder-avatar.png';
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => {
                      setValue('profilePhoto', undefined);
                      if (profileImageUrl && profileImageUrl.startsWith('blob:')) {
                        URL.revokeObjectURL(profileImageUrl);
                        setProfileImageUrl('');
                        setProfileImageFile(null);
                        console.log('🧹 Cleaned up local profile image URL on remove');
                      }
                    }}
                    className="absolute top-2 right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
                  >
                    <FiTrash2 size={16} />
                  </button>
                </div>
              ) : (
                <div className="w-48 h-48 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                  <div className="text-center">
                    <FiUser className="mx-auto h-12 w-12 text-gray-400" />
                    <span className="mt-2 block text-sm font-medium text-gray-900">No photo uploaded</span>
                  </div>
                </div>
              )}
            </div>
            
            {/* Upload New Photo */}
                          <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">Upload New Photo</h3>
                <div className="space-y-4">
                  <div>
                    <div className="relative">
                      <input 
                        id="profile-photo-upload"
                        type="file" 
                        onChange={handleProfilePhotoSelect}
                        disabled={isUploading['profile-photo']}
                        className={`block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-semibold ${
                          isUploading['profile-photo'] 
                            ? 'file:bg-gray-100 file:text-gray-400 cursor-not-allowed' 
                            : 'file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100'
                        }`}
                        accept=".jpg,.jpeg,.png"
                      />
                      {isUploading['profile-photo'] && (
                        <div className="absolute inset-0 bg-white bg-opacity-75 flex items-center justify-center rounded">
                          <div className="flex items-center space-x-2 text-blue-600">
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-blue-600 border-t-transparent"></div>
                            <span className="text-sm font-medium">Uploading photo...</span>
                          </div>
                        </div>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-gray-500">
                      {isUploading['profile-photo'] ? 'Please wait while your photo uploads...' : 'JPG or PNG, max 5MB - uploads automatically when selected'}
                    </p>
                  </div>
                </div>
              </div>
          </div>
        </div>

        {/* Emergency Contacts */}
        <div className={`bg-white shadow rounded-lg p-6 ${isSectionFlagged('dependents') ? 'border-l-4 border-orange-400 bg-orange-50' : ''}`}>
          <h2 className={`text-xl font-bold mb-4 flex items-center ${isSectionFlagged('dependents') ? 'text-orange-800' : ''}`}>
            Emergency Contacts
            <FieldFlagIndicator fieldName="dependents" />
            {isSectionFlagged('dependents') && <span className="ml-2 text-sm text-orange-600">(Requires Attention)</span>}
          </h2>
          {dependentFields.map((field, index) => (
            <div key={field.id} className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4 p-4 border rounded">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Full Names <span className="text-red-500">*</span></label>
                <input
                  {...register(`dependents.${index}.fullNames` as any, { required: 'Full Names is required' })}
                  placeholder="Full Names"
                  className="p-2 border border-gray-300 rounded w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Surname <span className="text-red-500">*</span></label>
                <input
                  {...register(`dependents.${index}.surname` as any, { required: 'Surname is required' })}
                  placeholder="Surname"
                  className="p-2 border border-gray-300 rounded w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Relation <span className="text-red-500">*</span></label>
                <select
                  {...register(`dependents.${index}.relation` as any, { required: 'Relation is required' })}
                  className="p-2 border border-gray-300 rounded w-full"
                >
                  <option value="">Select Relation</option>
                  {RELATION_OPTIONS.map(relation => (
                    <option key={relation} value={relation}>{relation}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Maiden Name</label>
                <input
                  {...register(`dependents.${index}.maidenName` as any)}
                  placeholder="Maiden Name"
                  className="p-2 border border-gray-300 rounded w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Contact Number</label>
                <input
                  {...register(`dependents.${index}.idNumber` as any, {
                    pattern: {
                      value: phonePattern,
                      message: 'Please enter a valid phone number'
                    }
                  })}
                  placeholder="Contact Number"
                  className="p-2 border border-gray-300 rounded w-full"
                />
              </div>
              <div className="md:col-span-5 flex justify-end">
                <button type="button" onClick={() => removeDependent(index)} className="p-2 text-red-500 hover:text-red-700">
                  <FiTrash2 />
                </button>
              </div>
            </div>
          ))}
          <button
            type="button"
            onClick={() => appendDependent({ id: '', fullNames: '', surname: '', maidenName: '', idNumber: '', relation: '' })}
            className="flex items-center text-blue-600 hover:text-blue-700"
          >
            <FiPlus className="mr-1" /> Add Emergency Contact
          </button>
        </div>



        {/* File Attachments */}
        <div className={`bg-white shadow rounded-lg p-6 ${isSectionFlagged('documents') ? 'border-l-4 border-orange-400 bg-orange-50' : ''}`}>
          <h2 className={`text-xl font-bold mb-4 flex items-center ${isSectionFlagged('documents') ? 'text-orange-800' : ''}`}>
            Required Documents
            <FieldFlagIndicator fieldName="attachments" />
            {isSectionFlagged('documents') && <span className="ml-2 text-sm text-orange-600">(Requires Attention)</span>}
          </h2>
          
          <p className="text-sm text-gray-600 mb-6">
            Please upload all required documents. Click "Choose File" next to each document type to upload.
          </p>

          <div className="space-y-4">
            {requiredDocumentTypes.map(docType => {
              const existingDoc = attachmentFields.find(field => field.documentType === docType);
              const isDocumentFlagged = fieldFlags.some(flag => 
                flag.fieldPath.includes('attachments') && flag.fieldPath.includes(docType)
              );
              const uploadingKey = `${docType}-upload`;
              
              return (
                <div key={docType} className={`border rounded-lg p-4 ${
                  isDocumentFlagged 
                    ? 'bg-orange-50 border-orange-200' 
                    : existingDoc 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-gray-50 border-gray-200'
                }`}>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        {existingDoc ? (
                          <FiCheck className="h-6 w-6 text-green-600" />
                        ) : (
                          <FiX className="h-6 w-6 text-red-500" />
                        )}
                      </div>
                      <div>
                        <h3 className="font-medium text-gray-900">{docType} <span className="text-red-500 ml-1">*</span></h3>
                        {existingDoc ? (
                          <p className="text-sm text-gray-600">{existingDoc.fileName}</p>
                        ) : (
                          <p className="text-sm text-red-600">Not uploaded</p>
                        )}
                        {isDocumentFlagged && (
                          <p className="text-sm text-orange-600">⚠️ Requires attention</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      {existingDoc && (
                        <button
                          type="button"
                          onClick={() => {
                            const index = attachmentFields.findIndex(field => field.id === existingDoc.id);
                            removeAttachment(index);
                          }}
                          className="p-2 text-red-500 hover:text-red-700"
                          title="Remove document"
                        >
                          <FiTrash2 />
                        </button>
                      )}
                      <div className="relative">
                        <input
                          type="file"
                          id={`upload-${docType}`}
                          className="hidden"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (file) {
                              const oldSelectedType = selectedDocumentType;
                              setSelectedDocumentType(docType as DocumentType);
                              setIsUploading(prev => ({ ...prev, [uploadingKey]: true }));
                              
                              try {
                                const uploadId = Date.now().toString();
                                const tempId = await ensureDraftId();
                                
                                const response = await getPresignedUrl(file.name, file.type, tempId, docType as DocumentType);
                                await uploadFile(response.uploadUrl, file);
                                
                                const newAttachment: DocumentAttachment = {
                                  id: uploadId,
                                  fileName: file.name,
                                  fileType: file.type,
                                  documentType: docType as DocumentType,
                                  fileKey: response.fileKey,
                                  downloadUrl: response.downloadUrl,
                                  uploadedAt: new Date().toISOString(),
                                };
                                
                                // If document already exists, replace it
                                if (existingDoc) {
                                  const index = attachmentFields.findIndex(field => field.id === existingDoc.id);
                                  removeAttachment(index);
                                }
                                
                                appendAttachment(newAttachment);
                                toast.success(`${docType} uploaded successfully.`);
                                
                                // Reset file input
                                const fileInput = document.getElementById(`upload-${docType}`) as HTMLInputElement;
                                if (fileInput) fileInput.value = '';
                              } catch (error) {
                                console.error('❌ Upload error:', error);
                                toast.error(`Failed to upload ${docType}.`);
                              } finally {
                                setIsUploading(prev => ({ ...prev, [uploadingKey]: false }));
                                setSelectedDocumentType(oldSelectedType);
                              }
                            }
                          }}
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        />
                        <label
                          htmlFor={`upload-${docType}`}
                          className={`px-4 py-2 rounded-lg cursor-pointer inline-flex items-center text-sm font-medium transition-colors ${
                            isUploading[uploadingKey]
                              ? 'bg-gray-400 text-white cursor-not-allowed'
                              : existingDoc
                                ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                : 'bg-blue-600 text-white hover:bg-blue-700'
                          }`}
                        >
                          <FiUpload className="mr-2 h-4 w-4" />
                          {isUploading[uploadingKey] ? 'Uploading...' : existingDoc ? 'Replace' : 'Choose File'}
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Optional Documents Section */}
          <div className="mt-8">
            <h3 className="text-lg font-medium mb-4 text-gray-700">Optional Documents</h3>
            <div className="space-y-4">
              {optionalDocumentTypes.map(docType => {
                const existingDoc = attachmentFields.find(field => field.documentType === docType);
                const uploadingKey = `${docType}-upload`;
                
                return (
                  <div key={docType} className={`border rounded-lg p-4 ${
                    existingDoc 
                      ? 'bg-green-50 border-green-200' 
                      : 'bg-gray-50 border-gray-200'
                  }`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0">
                          {existingDoc ? (
                            <FiCheck className="h-6 w-6 text-green-600" />
                          ) : (
                            <div className="h-6 w-6 border-2 border-gray-300 rounded" />
                          )}
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">{docType}</h3>
                          {existingDoc ? (
                            <p className="text-sm text-gray-600">{existingDoc.fileName}</p>
                          ) : (
                            <p className="text-sm text-gray-500">Optional</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {existingDoc && (
                          <button
                            type="button"
                            onClick={() => {
                              const index = attachmentFields.findIndex(field => field.id === existingDoc.id);
                              removeAttachment(index);
                            }}
                            className="p-2 text-red-500 hover:text-red-700"
                            title="Remove document"
                          >
                            <FiTrash2 />
                          </button>
                        )}
                        <div className="relative">
                          <input
                            type="file"
                            id={`upload-${docType}`}
                            className="hidden"
                            onChange={async (e) => {
                              const file = e.target.files?.[0];
                              if (file) {
                                const oldSelectedType = selectedDocumentType;
                                setSelectedDocumentType(docType as DocumentType);
                                setIsUploading(prev => ({ ...prev, [uploadingKey]: true }));
                                
                                try {
                                  const uploadId = Date.now().toString();
                                  const tempId = await ensureDraftId();
                                  
                                  const response = await getPresignedUrl(file.name, file.type, tempId, docType as DocumentType);
                                  await uploadFile(response.uploadUrl, file);
                                  
                                  const newAttachment: DocumentAttachment = {
                                    id: uploadId,
                                    fileName: file.name,
                                    fileType: file.type,
                                    documentType: docType as DocumentType,
                                    fileKey: response.fileKey,
                                    downloadUrl: response.downloadUrl,
                                    uploadedAt: new Date().toISOString(),
                                  };
                                  
                                  // If document already exists, replace it
                                  if (existingDoc) {
                                    const index = attachmentFields.findIndex(field => field.id === existingDoc.id);
                                    removeAttachment(index);
                                  }
                                  
                                  appendAttachment(newAttachment);
                                  toast.success(`${docType} uploaded successfully.`);
                                  
                                  // Reset file input
                                  const fileInput = document.getElementById(`upload-${docType}`) as HTMLInputElement;
                                  if (fileInput) fileInput.value = '';
                                } catch (error) {
                                  console.error('❌ Upload error:', error);
                                  toast.error(`Failed to upload ${docType}.`);
                                } finally {
                                  setIsUploading(prev => ({ ...prev, [uploadingKey]: false }));
                                  setSelectedDocumentType(oldSelectedType);
                                }
                              }
                            }}
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          />
                          <label
                            htmlFor={`upload-${docType}`}
                            className={`px-4 py-2 rounded-lg cursor-pointer inline-flex items-center text-sm font-medium transition-colors ${
                              isUploading[uploadingKey]
                                ? 'bg-gray-400 text-white cursor-not-allowed'
                                : existingDoc
                                  ? 'bg-blue-100 text-blue-700 hover:bg-blue-200'
                                  : 'bg-blue-600 text-white hover:bg-blue-700'
                            }`}
                          >
                            <FiUpload className="mr-2 h-4 w-4" />
                            {isUploading[uploadingKey] ? 'Uploading...' : existingDoc ? 'Replace' : 'Choose File'}
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>File Requirements:</strong> Please upload clear, readable documents in PDF, Word, or image format (max 10MB each). 
              Files are automatically uploaded when selected - no additional steps needed.
            </p>
          </div>
        </div>

        <div className="flex justify-between items-center">
          <div className="flex gap-4">
            {!isResubmission && (
              <button
                type="button"
                onClick={clearForm}
                className="px-6 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 transition-colors border border-red-300 flex items-center gap-2"
                disabled={isLoading}
              >
                <FiRotateCcw className="w-4 h-4" />
                Clear Form
              </button>
            )}
          </div>
          
          <div className="flex gap-4">
            {!isResubmission && (
              <button
                type="button"
                onClick={saveDraft}
                className="px-6 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300 transition-colors"
                disabled={isLoading}
              >
                Save Draft
              </button>
            )}
            <button
              type="submit"
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              disabled={isLoading}
            >
              {isLoading ? 'Submitting...' : (isResubmission ? 'Update Application' : 'Register')}
            </button>
          </div>
        </div>
      </form>
      </div>
    </>
  );
};

export default EmployeeFormPage;
